import React from 'react';
import { sortBy } from 'lodash';
import moment from 'moment-timezone';
import {
  faBell,
  faCaretCircleLeft,
  faCaretCircleRight,
  faChartLine,
  faList,
  faTable
} from '@fortawesome/pro-solid-svg-icons';

import Icon from '../../atoms/icon';
import Button from '../../molecules/button';
import ErrorBoundary from '../../molecules/error-boundary';
import Alert from '../../organisms/alert';
import SessionGraphs from '../../organisms/graph';
import { AppKey } from '../../../libs/apps';
import AppConfigs from '../../../libs/apps/configs';

import { useTriagePatient } from '../../../contexts/triage/patient';
import {
  ICustomProgram,
  ICustomSchedule,
  getRotatingQuestionNode
} from '../../../libs/composer';
import RotatingQuestionsSummary from '../../organisms/table/rotating-questions-summary';

interface ProgramDataProps {
  appKey: AppKey;
  chartReflowTrigger: boolean;
  isSidebarExpanded: boolean;
  setIsSidebarExpanded: (isExpanded: boolean) => void;
  customProgram?: ICustomProgram;
}

const ProgramData: React.FC<ProgramDataProps> = ({
  appKey,
  chartReflowTrigger,
  isSidebarExpanded,
  setIsSidebarExpanded,
  customProgram
}): JSX.Element => {
  const { patientData: triage } = useTriagePatient();
  const { patient, alerts: _alerts } = triage;
  // Schedule might be in triage or passed separately - handle both cases
  const schedule = (triage as any)?.schedule;
  // Check if this program has rotating questions
  const hasRotatingQuestions = React.useMemo(() => {
    if (!customProgram?.source) {
      return false;
    }
    try {
      const rotatingNode = getRotatingQuestionNode(customProgram.source);
      return rotatingNode !== null;
    } catch {
      return false;
    }
  }, [customProgram?.source]);
  const alerts = sortBy(
    _alerts.filter((alert) => alert.app === appKey),
    (alert) => {
      const time = moment(alert.createdAt);
      return -time.valueOf();
    }
  );
  const config = customProgram ? AppConfigs['generic'] : AppConfigs[appKey];
  const params = {
    patient: patient.id,
    programId: customProgram?.id ?? null
  };
  const appEntry = triage?.apps?.[appKey];
  const appSchedule = appEntry?.schedule;
  const AppGraphs = config?.graph || null;
  const AppSummary = config?.summary || null;
  const AppTable = config?.table || null;
  const hasExtraTables = config?.table;
  const AppExtraTables: JSX.Element[] = [];
  if (config?.extraTables) {
    for (const key of Object.keys(config.extraTables)) {
      const { label, table: AppExtraTable, visible } = config.extraTables[key];
      if (visible && !visible(appSchedule)) {
        continue;
      }
      AppExtraTables.push(
        <React.Fragment key={key}>
          <h4>
            <Icon icon={faTable} /> {label} Responses
          </h4>
          <AppExtraTable params={params} />
        </React.Fragment>
      );
    }
  }
  return (
    <div>
      {appKey !== 'surveyqual' && (
        <ErrorBoundary>
          <h4>
            <Icon icon={faBell} /> Recent Alerts
          </h4>
          {alerts.map((alert) => (
            <Alert
              key={alert.id}
              alert={alert}
              hideViewProgram
              patient={patient}
            />
          ))}
        </ErrorBoundary>
      )}
      <ErrorBoundary>
        {AppSummary ? (
          <React.Fragment>
            <h4 className="mb-2">
              <Icon icon={faList} /> Summary
            </h4>
            <AppSummary params={params} />
          </React.Fragment>
        ) : null}
      </ErrorBoundary>
      {appKey !== 'surveyqual' && (
        <ErrorBoundary>
          <h4>
            <Icon icon={faChartLine} /> Graphs & Report
          </h4>
          {AppGraphs ? (
            <AppGraphs
              chartReflowTrigger={chartReflowTrigger}
              params={{
                ...params,
                app: appKey
              }}
            />
          ) : (
            <SessionGraphs
              url="/data/data_by_range/"
              chartReflowTrigger={chartReflowTrigger}
              graphs={[]}
              params={{
                ...params,
                app: appKey
              }}
              programId={customProgram?.id ?? null}
            />
          )}
        </ErrorBoundary>
      )}
      <ErrorBoundary>
        {AppTable ? (
          <React.Fragment>
            <h4>
              <Icon icon={faTable} /> All Responses
              <Button
                onClick={() => setIsSidebarExpanded(!isSidebarExpanded)}
                className="ml-4"
                intent="primary"
                variant="minimal"
                bare
              >
                <Icon
                  icon={
                    isSidebarExpanded ? faCaretCircleLeft : faCaretCircleRight
                  }
                />
                &nbsp;
                {isSidebarExpanded ? 'Collapse' : 'Expand'} Sidebar
              </Button>
            </h4>
            <AppTable params={params} customProgram={customProgram} />
          </React.Fragment>
        ) : null}
      </ErrorBoundary>
      <ErrorBoundary>{hasExtraTables ? AppExtraTables : null}</ErrorBoundary>
      {hasRotatingQuestions && customProgram && schedule && (
        <ErrorBoundary>
          <h4>
            <Icon icon={faTable} /> Rotating Questions Summary
          </h4>
          <RotatingQuestionsSummary
            customProgram={customProgram}
            schedule={schedule as ICustomSchedule}
            params={params}
          />
        </ErrorBoundary>
      )}
    </div>
  );
};

export default ProgramData;
